import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import HelmetWrapper from 'components/HelmetWrapper';
import { useTranslation } from 'react-i18next';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import MembersContext from '../../providers/Members/Members.context';
import WaitingListTable from './components/WaitingListTable';
import { Collapsible, Loader } from 'ncoded-component-library';
import WaitingRoomEmptyState from './components/WaitingRoomEmptyState';
import EditShareableLinkProvider from '../../providers/EditShareableLink';
import CreateShareableLinkProvider from '../../providers/CreateShareableLink';
import useMatchMedia from 'hooks/useMatchMedia';
import { MembersMobile } from '../../subrouters/Members/pages/Members/components/MembersMobile';
import { MembersDesktop } from '../../subrouters/Members/pages/Members/components/MembersDesktop';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import WaitingListExpiredTable from './components/WaitingListExpiredTable';
import OnboardingContext from '../../providers/Onboarding/Onboarding.context';
import { DataRoomShareableLink } from 'models/ShareableLink';

import './WaitingRoom.styles.scss';
import './WaitingRoom.styles.responsive.scss';

type WaitingRoomProps = {
  className?: string;
};

const WaitingRoom: React.FC<WaitingRoomProps> = (props) => {
  const { className } = props;

  const {
    dataRoom: { dealRoom },
  } = useContext(DataRoomContext);

  const {
    isLoading,
    isLoadingWaitingList,
    isLoadingWaitingListExpired,
    requestedMembersCount,
    waitingRoomItemsCount,
    waitingRoomItemsExpiredCount,
    removeWaitingRoomItem,
    removeWaitingRoomItemExpired,
    updateShareableLinkInWaitingItem,
  } = useContext(MembersContext);
  const { contentCard } = useContext(OnboardingContext);

  const isTablet = useMatchMedia('isTablet');

  const classes = classNames('waiting-room', 'page', className);

  const { t } = useTranslation();

  const showLoader =
    isLoadingWaitingList || isLoading || isLoadingWaitingListExpired;

  const showEmptyState = !(
    waitingRoomItemsCount ||
    waitingRoomItemsExpiredCount ||
    requestedMembersCount
  );

  const onCreateNewSecureLink = useCallback(
    (_: DataRoomShareableLink, dataRoomShareableLinkRequestId: string) => {
      removeWaitingRoomItem(dataRoomShareableLinkRequestId);
      removeWaitingRoomItemExpired(dataRoomShareableLinkRequestId);
    },
    [removeWaitingRoomItem, removeWaitingRoomItemExpired],
  );

  return (
    <div className={classes}>
      <HelmetWrapper title={`${t('waitingRoom')} - ${dealRoom?.name}`} />
      <h2>{t('waitingRoom')}</h2>
      {contentCard}

      {showLoader ? (
        <Loader />
      ) : showEmptyState ? (
        <WaitingRoomEmptyState />
      ) : (
        <>
          {!!(waitingRoomItemsCount || waitingRoomItemsExpiredCount) && (
            <div className="waiting-room__secure-links">
              <CreateShareableLinkProvider
                onSuccessFullyCreated={onCreateNewSecureLink}
              >
                <EditShareableLinkProvider
                  updateShareableLink={updateShareableLinkInWaitingItem}
                >
                  <Collapsible
                    defaultOpen
                    className="waiting-room__secure-links__collapsible white-panel"
                    trigger={
                      <div className="trigger">
                        <div className="trigger__title">
                          <p className="name">{t('secureLinks')}</p>
                        </div>

                        <div className="trigger__actions">
                          <ChevrondownIcon />
                        </div>
                      </div>
                    }
                  >
                    {!!waitingRoomItemsCount && (
                      <>
                        <p>{t('requestsAccessExistingSl')}</p>
                        <WaitingListTable />
                      </>
                    )}

                    {!!waitingRoomItemsExpiredCount && (
                      <>
                        <p>{t('requestsAccessExpiredSl')}</p>
                        <WaitingListExpiredTable />
                      </>
                    )}
                  </Collapsible>
                </EditShareableLinkProvider>
              </CreateShareableLinkProvider>
            </div>
          )}

          {!!requestedMembersCount && (
            <div className="waiting-room__members">
              {isTablet ? (
                <MembersMobile onlyNew />
              ) : (
                <MembersDesktop onlyNew />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WaitingRoom;
